import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { WorkforceTableLayout } from './WorkforceTableLayout';

interface WorkforceItem {
  team?: string;
  assignedTeam?: string;
  function?: string;
  quantity?: number;
  observations?: string;
  type?: 'direta' | 'terceirizada' | 'team' | 'individual';
  name?: string;
  // Team entry properties
  teamId?: string;
  teamName?: string;
  totalMembers?: number;
  members?: Array<{
    name: string;
    role: string;
    qty: number;
  }>;
  employeeType?: 'direta' | 'terceirizada';
}

interface WorkforceCardsLayoutProps {
  workforce: WorkforceItem[];
  title?: string;
  showTotals?: boolean;
  viewMode?: 'cards' | 'table';
  onEdit?: (index: number) => void;
  onDelete?: (index: number) => void;
}

export const WorkforceCardsLayout: React.FC<WorkforceCardsLayoutProps> = ({
  workforce,
  title = "Mão de Obra",
  showTotals = true,
  viewMode = 'cards',
  onEdit,
  onDelete
}) => {

  // Separar por tipo de entrada primeiro
  const teamEntries = workforce.filter(item => item.type === 'team');
  const individualEntries = workforce.filter(item => item.type !== 'team');

  // Aplicar a lógica de fallback apenas para entradas individuais
  const individualsWithType = individualEntries.map(item => ({
    ...item,
    type: item.type || (item.team?.toLowerCase().includes('terceirizada') ? 'terceirizada' : 'direta')
  }));

  // Separar entradas individuais por tipo de funcionário
  const diretaIndividuals = individualsWithType.filter(item => item.type === 'direta');
  const terceiraIndividuals = individualsWithType.filter(item => item.type === 'terceirizada');

  // Separar entradas de equipe por tipo de funcionário
  const diretaTeams = teamEntries.filter(item => item.employeeType === 'direta');
  const terceiraTeams = teamEntries.filter(item => item.employeeType === 'terceirizada');

  // Consolidar apenas as entradas individuais (manter lógica existente)
  const consolidateIndividuals = (items: WorkforceItem[]) => {
    const consolidatedTeams = new Map<string, WorkforceItem[]>();
    const individualsWithoutTeam: WorkforceItem[] = [];
    
    items.forEach(item => {
      const teamName = (item.assignedTeam || item.team || '').trim();
      
      if (!teamName || teamName === 'Sem equipe') {
        individualsWithoutTeam.push(item);
        return;
      }
      
      if (!consolidatedTeams.has(teamName)) {
        consolidatedTeams.set(teamName, []);
      }
      consolidatedTeams.get(teamName)!.push(item);
    });
    
    if (individualsWithoutTeam.length > 0) {
      consolidatedTeams.set('Sem equipe', individualsWithoutTeam);
    }
    
    return consolidatedTeams;
  };

  // Aplicar consolidação apenas para indivíduos
  const diretaIndividualsMap = consolidateIndividuals(diretaIndividuals);
  const terceiraIndividualsMap = consolidateIndividuals(terceiraIndividuals);
  
  const diretaIndividualTeams = Object.fromEntries(
    Array.from(diretaIndividualsMap.entries()).filter(([teamName]) => teamName !== 'Sem equipe')
  );
  const terceiraIndividualTeams = Object.fromEntries(
    Array.from(terceiraIndividualsMap.entries()).filter(([teamName]) => teamName !== 'Sem equipe')
  );
  
  const diretaWithoutTeam = diretaIndividualsMap.get('Sem equipe') || [];
  const terceiraWithoutTeam = terceiraIndividualsMap.get('Sem equipe') || [];

  // Calcular totais considerando ambos os tipos
  const totalDireta = diretaTeams.reduce((sum, team) => sum + (team.totalMembers || 0), 0) +
                     diretaIndividuals.reduce((sum, item) => sum + (item.quantity || 0), 0);
  
  const totalTerceira = terceiraTeams.reduce((sum, team) => sum + (team.totalMembers || 0), 0) +
                        terceiraIndividuals.reduce((sum, item) => sum + (item.quantity || 0), 0);

  // Renderizar card de equipe (type: "team")
  const renderTeamEntryCard = (teamEntry: WorkforceItem, index: number) => {
    const isDirecta = teamEntry.employeeType === 'direta';
    const borderColor = isDirecta ? 'border-blue-200' : 'border-orange-200';
    const bgColor = isDirecta ? 'bg-blue-50/30' : 'bg-orange-50';
    const badgeColor = isDirecta ? 'default' : 'secondary';
    const badgeClass = isDirecta ? '' : 'bg-orange-100 text-orange-700 hover:bg-orange-200';
    const titleColor = isDirecta ? 'text-blue-700' : 'text-orange-700';

    const uniqueRoles = [...new Set(teamEntry.members?.map(m => m.role) || [])];
    const displayFunction = uniqueRoles.length === 1 ? uniqueRoles[0] : 
                           uniqueRoles.length > 1 ? "Diversos" : "Sem função";

    return (
      <HoverCard key={`team-entry-${teamEntry.teamId || index}`} openDelay={1000}>
        <HoverCardTrigger asChild>
          <Card 
            className={`hover:shadow-md transition-shadow max-w-36 border-2 ${borderColor} ${bgColor} cursor-pointer relative`}
            onClick={() => {
              if (onEdit) {
                const originalIndex = workforce.findIndex(item => 
                  item.type === 'team' && item.teamId === teamEntry.teamId
                );
                if (originalIndex >= 0) {
                  onEdit(originalIndex);
                }
              }
            }}
          >
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute -top-0.5 -right-0.5 h-3 w-3 p-0 rounded-full bg-destructive/90 hover:bg-destructive shadow-sm border border-background/50 text-destructive-foreground hover:scale-105 transition-all duration-200 z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  const originalIndex = workforce.findIndex(item => 
                    item.type === 'team' && item.teamId === teamEntry.teamId
                  );
                  if (originalIndex >= 0) {
                    onDelete(originalIndex);
                  }
                }}
              >
                <X className="h-2 w-2" />
              </Button>
            )}
            <CardContent className="p-2">
              <div className="space-y-1.5">
                <Badge 
                  variant={badgeColor} 
                  className={`w-full justify-center text-xs ${badgeClass}`}
                >
                  {teamEntry.teamName}
                </Badge>
                <div className={`text-lg font-bold text-center ${isDirecta ? 'text-blue-600' : 'text-orange-600'}`}>
                  Total: {teamEntry.totalMembers}
                </div>
                <div className="text-xs text-center text-muted-foreground">
                  {displayFunction}
                </div>
                {teamEntry.observations && (
                  <div className="text-[10px] text-muted-foreground mt-1.5 p-1.5 bg-muted/30 rounded">
                    {teamEntry.observations}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </HoverCardTrigger>
        <HoverCardContent className="w-80">
          <div className="space-y-3">
            <div>
              <h4 className={`font-semibold ${titleColor}`}>Equipe: {teamEntry.teamName}</h4>
              <p className="text-sm text-muted-foreground">
                Mão de obra {isDirecta ? 'própria' : 'terceirizada'}
              </p>
            </div>
            <div className="space-y-2">
              <div className="text-sm">
                <span className="font-medium">Total de pessoas:</span> {teamEntry.totalMembers}
              </div>
              <div className="text-sm">
                <span className="font-medium">Composição:</span>
                <ul className="list-disc list-inside ml-2 mt-1">
                  {teamEntry.members?.map((member, idx) => (
                    <li key={idx} className="text-xs text-muted-foreground">
                      {member.qty} {member.role} ({member.qty > 1 ? 'pessoas' : 'pessoa'})
                    </li>
                  ))}
                </ul>
              </div>
              {teamEntry.observations && (
                <div className="text-sm">
                  <span className="font-medium">Observações:</span>
                  <p className="text-xs text-muted-foreground mt-1">
                    {teamEntry.observations}
                  </p>
                </div>
              )}
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  };

  // Improved team card rendering function (for consolidated individuals)
  const renderTeamCard = (teamName: string, members: WorkforceItem[], type: 'direta' | 'terceirizada') => {
    const teamTotal = members.reduce((sum, item) => sum + (item.quantity || 0), 0);
    
    // Group functions and their quantities
    const functionSummary = members.reduce((acc, member) => {
      const func = member.function || 'Sem função';
      acc[func] = (acc[func] || 0) + (member.quantity || 0);
      return acc;
    }, {} as Record<string, number>);
    
    const uniqueFunctions = Object.keys(functionSummary);
    const displayFunction = uniqueFunctions.length === 1 ? uniqueFunctions[0] : 
                          uniqueFunctions.length > 1 ? "Diversos" : "Sem função";
    
    const borderColor = type === 'direta' ? 'border-blue-200' : 'border-orange-200';
    const bgColor = type === 'direta' ? 'bg-blue-50/30' : 'bg-orange-50';
    const badgeColor = type === 'direta' ? 'default' : 'secondary';
    const badgeClass = type === 'direta' ? '' : 'bg-orange-100 text-orange-700 hover:bg-orange-200';
    const titleColor = type === 'direta' ? 'text-blue-700' : 'text-orange-700';
    
    return (
      <HoverCard key={`${type}-${teamName}`} openDelay={1000}>
        <HoverCardTrigger asChild>
          <Card 
            className={`hover:shadow-md transition-shadow max-w-36 border-2 ${borderColor} ${bgColor} cursor-pointer relative`}
            onClick={() => {
              if (onEdit && members.length > 0) {
                const firstMember = members[0];
                const firstMemberIndex = workforce.findIndex(item => 
                  item.name === firstMember.name &&
                  item.function === firstMember.function &&
                  (item.assignedTeam || item.team) === (firstMember.assignedTeam || firstMember.team) &&
                  item.quantity === firstMember.quantity
                );
                
                if (firstMemberIndex >= 0) {
                  onEdit(firstMemberIndex);
                }
              }
            }}
          >
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute -top-0.5 -right-0.5 h-3 w-3 p-0 rounded-full bg-destructive/90 hover:bg-destructive shadow-sm border border-background/50 text-destructive-foreground hover:scale-105 transition-all duration-200 z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteTeam(teamName, type);
                }}
              >
                <X className="h-2 w-2" />
              </Button>
            )}
            <CardContent className="p-2">
              <div className="space-y-1.5">
                <Badge 
                  variant={badgeColor} 
                  className={`w-full justify-center text-xs ${badgeClass}`}
                >
                  {teamName}
                </Badge>
                <div className={`text-lg font-bold text-center ${type === 'direta' ? 'text-blue-600' : 'text-orange-600'}`}>
                  Total: {teamTotal}
                </div>
                <div className="text-xs text-center text-muted-foreground">
                  {uniqueFunctions.length === 1 ? uniqueFunctions[0] : 'Diversos'}
                </div>
                {members.some(m => m.observations) && (
                  <div className="text-[10px] text-muted-foreground mt-1.5 p-1.5 bg-muted/30 rounded">
                    {members.find(m => m.observations)?.observations}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </HoverCardTrigger>
        <HoverCardContent className="w-80">
          <div className="space-y-3">
            <div>
              <h4 className={`font-semibold ${titleColor}`}>Equipe: {teamName}</h4>
              <p className="text-sm text-muted-foreground">
                Mão de obra {type === 'direta' ? 'própria' : 'terceirizada'}
              </p>
            </div>
            <div className="space-y-2">
              <div className="text-sm">
                <span className="font-medium">Total de pessoas:</span> {teamTotal}
              </div>
              <div className="text-sm">
                <span className="font-medium">Composição:</span>
                <ul className="list-disc list-inside ml-2 mt-1">
                  {Object.entries(functionSummary).map(([func, qty]) => (
                    <li key={func} className="text-xs text-muted-foreground">
                      {qty} {func} ({qty > 1 ? 'pessoas' : 'pessoa'})
                    </li>
                  ))}
                </ul>
              </div>
              {members.some(m => m.observations) && (
                <div className="text-sm">
                  <span className="font-medium">Observações:</span>
                  <p className="text-xs text-muted-foreground mt-1">
                    {members.find(m => m.observations)?.observations}
                  </p>
                </div>
              )}
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  };

  const handleDeleteTeam = (teamName: string, type: 'direta' | 'terceirizada') => {
    const teamItems = type === 'direta' ? diretaIndividualTeams[teamName] : terceiraIndividualTeams[teamName];
    if (!teamItems || !onDelete) return;

    // Delete all items in this team (reverse order to maintain indices)
    for (let i = teamItems.length - 1; i >= 0; i--) {
      const teamItem = teamItems[i];
      const originalIndex = workforce.findIndex(item => 
        item.name === teamItem.name &&
        item.function === teamItem.function &&
        (item.assignedTeam || item.team) === (teamItem.assignedTeam || teamItem.team) &&
        item.quantity === teamItem.quantity
      );
      if (originalIndex >= 0) {
        onDelete(originalIndex);
      }
    }
  };

  const handleDeleteIndividual = (item: WorkforceItem) => {
    if (!onDelete) return;
    const originalIndex = workforce.findIndex(workItem => 
      workItem.name === item.name &&
      workItem.function === item.function &&
      (workItem.assignedTeam || workItem.team) === (item.assignedTeam || item.team) &&
      workItem.quantity === item.quantity
    );
    if (originalIndex >= 0) {
      onDelete(originalIndex);
    }
  };

  // Cards view component
  const CardsView = () => (
    <div className="space-y-4">      
      {/* Direct workforce row */}
      {(diretaTeams.length > 0 || Object.keys(diretaIndividualTeams).length > 0 || diretaWithoutTeam.length > 0) && (
        <div>
          <div className="text-sm font-medium text-blue-700 mb-2">Próprios</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {/* Team entry cards (type: "team") */}
            {diretaTeams.map((teamEntry, index) => 
              renderTeamEntryCard(teamEntry, index)
            )}
            
            {/* Individual team cards (consolidated from individual entries) */}
            {Object.entries(diretaIndividualTeams).map(([teamName, members]) => 
              renderTeamCard(teamName, members, 'direta')
            )}
            
            {/* Individual cards for items without team */}
            {diretaWithoutTeam.map((item, index) => (
              <HoverCard key={`sem-equipe-direta-${index}`} openDelay={1000}>
                <HoverCardTrigger asChild>
                  <Card 
                    className="hover:shadow-md transition-shadow max-w-36 border-blue-200 bg-blue-50/30 cursor-pointer relative"
                     onClick={() => {
                       if (onEdit) {
                         const originalIndex = workforce.findIndex(workItem => 
                           workItem.name === item.name &&
                           workItem.function === item.function &&
                           (workItem.assignedTeam || workItem.team) === (item.assignedTeam || item.team) &&
                           workItem.quantity === item.quantity
                         );
                         if (originalIndex >= 0) {
                           onEdit(originalIndex);
                         }
                       }
                     }}
                  >
                    {onDelete && (
                       <Button
                         variant="ghost"
                         size="sm"
                         className="absolute -top-0.5 -right-0.5 h-3 w-3 p-0 rounded-full bg-destructive/90 hover:bg-destructive shadow-sm border border-background/50 text-destructive-foreground hover:scale-105 transition-all duration-200 z-10"
                         onClick={(e) => {
                           e.stopPropagation();
                           handleDeleteIndividual(item);
                         }}
                       >
                         <X className="h-2 w-2" />
                       </Button>
                    )}
                    <CardContent className="p-2">
                      <div className="space-y-1.5">
                        <div className="font-medium text-sm text-center text-primary">
                          {item.function || "Sem função"}
                        </div>
                        <div className="text-2xl font-bold text-center text-primary">
                          {item.quantity || 0}
                        </div>
                        {item.observations && (
                          <div className="text-[10px] text-muted-foreground mt-1.5 p-1.5 bg-muted/30 rounded">
                            {item.observations}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </HoverCardTrigger>
                <HoverCardContent className="w-80">
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-semibold text-blue-700">Sem equipe definida</h4>
                      <p className="text-sm text-muted-foreground">Mão de obra própria</p>
                    </div>
                    <div className="space-y-2">
                      <div className="text-sm">
                        <span className="font-medium">Função:</span> {item.function || "Sem função"}
                      </div>
                      <div className="text-sm">
                        <span className="font-medium">Quantidade:</span> {item.quantity || 0} pessoa(s)
                      </div>
                      {item.observations && (
                        <div className="text-sm">
                          <span className="font-medium">Observações:</span>
                          <p className="text-xs text-muted-foreground mt-1">
                            {item.observations}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            ))}
            
            {/* Direct summary card */}
            {showTotals && totalDireta > 0 && (
              <Card className="border-2 border-blue-200 bg-blue-50 hover:shadow-md transition-shadow max-w-36">
                <CardContent className="p-2">
                  <div className="space-y-1.5">
                    <div className="font-medium text-base text-center text-blue-700">Total Direta</div>
                    <div className="text-2xl font-bold text-center text-blue-600">
                      {totalDireta}
                    </div>
                    <div className="text-xs text-blue-600 text-center">
                      pessoas
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
      
      {/* Third-party workforce row */}
      {(terceiraTeams.length > 0 || Object.keys(terceiraIndividualTeams).length > 0 || terceiraWithoutTeam.length > 0) && (
        <div>
          <div className="text-sm font-medium text-orange-700 mb-2">Terceirizados</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {/* Team entry cards (type: "team") */}
            {terceiraTeams.map((teamEntry, index) => 
              renderTeamEntryCard(teamEntry, index)
            )}
            
            {/* Individual team cards (consolidated from individual entries) */}
            {Object.entries(terceiraIndividualTeams).map(([teamName, members]) => 
              renderTeamCard(teamName, members, 'terceirizada')
            )}
            
            {/* Individual cards for items without team */}
            {terceiraWithoutTeam.map((item, index) => (
              <HoverCard key={`sem-equipe-terceira-${index}`} openDelay={1000}>
                <HoverCardTrigger asChild>
                  <Card 
                    className="hover:shadow-md transition-shadow max-w-36 border-2 border-orange-200 bg-orange-50 cursor-pointer relative"
                     onClick={() => {
                       if (onEdit) {
                         const originalIndex = workforce.findIndex(workItem => 
                           workItem.name === item.name &&
                           workItem.function === item.function &&
                           (workItem.assignedTeam || workItem.team) === (item.assignedTeam || item.team) &&
                           workItem.quantity === item.quantity
                         );
                         if (originalIndex >= 0) {
                           onEdit(originalIndex);
                         }
                       }
                     }}
                  >
                    {onDelete && (
                       <Button
                         variant="ghost"
                         size="sm"
                         className="absolute -top-0.5 -right-0.5 h-3 w-3 p-0 rounded-full bg-destructive/90 hover:bg-destructive shadow-sm border border-background/50 text-destructive-foreground hover:scale-105 transition-all duration-200 z-10"
                         onClick={(e) => {
                           e.stopPropagation();
                           handleDeleteIndividual(item);
                         }}
                       >
                         <X className="h-2 w-2" />
                       </Button>
                    )}
                    <CardContent className="p-2">
                      <div className="space-y-1.5">
                         <div className="font-medium text-sm text-center text-orange-700">
                           {item.function || "Sem função"}
                         </div>
                         <div className="text-2xl font-bold text-center text-orange-600">
                           {item.quantity || 0}
                         </div>
                        {item.observations && (
                          <div className="text-[10px] text-muted-foreground mt-1.5 p-1.5 bg-muted/30 rounded">
                            {item.observations}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </HoverCardTrigger>
                <HoverCardContent className="w-80">
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-semibold text-orange-700">Sem equipe definida</h4>
                      <p className="text-sm text-muted-foreground">Mão de obra terceirizada</p>
                    </div>
                    <div className="space-y-2">
                      <div className="text-sm">
                        <span className="font-medium">Função:</span> {item.function || "Sem função"}
                      </div>
                      <div className="text-sm">
                        <span className="font-medium">Quantidade:</span> {item.quantity || 0} pessoa(s)
                      </div>
                      {item.observations && (
                        <div className="text-sm">
                          <span className="font-medium">Observações:</span>
                          <p className="text-xs text-muted-foreground mt-1">
                            {item.observations}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            ))}
            
            {/* Third-party summary card */}
            {showTotals && totalTerceira > 0 && (
              <Card className="border-2 border-orange-200 bg-orange-50 hover:shadow-md transition-shadow max-w-36">
                <CardContent className="p-2">
                  <div className="space-y-1.5">
                    <div className="font-medium text-base text-center text-orange-700">Total Terceirizada</div>
                    <div className="text-2xl font-bold text-center text-orange-600">
                      {totalTerceira}
                    </div>
                    <div className="text-xs text-orange-600 text-center">
                      pessoas
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (viewMode === 'table') {
    return (
      <div className="w-full">
        <WorkforceTableLayout
          workforce={workforce}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </div>
    );
  }

  return <CardsView />;
};